import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeftIcon, PrinterIcon, MailIcon } from '@heroicons/react/outline';
import { fetchOrderById, updateOrderStatus } from './orders-controller/order-controller';

const OrderDetailPage = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const printRef = useRef(null);
  const orderItemsRef = useRef(null);

  // Handle print functionality using a simpler approach with a new window
  const handlePrint = () => {
    if (!order) return;

    // Calculate totals - using data from the order
    const subtotal = order.totalHt || order.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const tax = order.totalTva || 0;
    const total = order.totalTTC || subtotal + tax;

    // Create a new window for printing
    const printWindow = window.open('', '_blank', 'width=800,height=600');

    // Create the invoice HTML content in French
    const invoiceContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Facture - Commande ${order.reference}</title>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            color: #333;
          }
          .invoice-header {
            text-align: center;
            margin-bottom: 30px;
          }
          .invoice-header h1 {
            margin: 0;
            font-size: 24px;
            color: #333;
          }
          .invoice-header p {
            margin: 5px 0;
            color: #666;
          }
          .order-info {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            border-bottom: 1px solid #eee;
            padding-bottom: 15px;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          th {
            background-color: #f8f9fa;
            text-align: left;
            padding: 10px;
            font-size: 14px;
            font-weight: 600;
            color: #555;
            text-transform: uppercase;
          }
          td {
            padding: 10px;
            border-bottom: 1px solid #eee;
          }
          .item-name {
            font-weight: 600;
          }
          .item-sku {
            color: #777;
            font-size: 12px;
          }
          .totals {
            width: 300px;
            margin-left: auto;
          }
          .totals div {
            display: flex;
            justify-content: space-between;
            padding: 7px 0;
          }
          .total-row {
            border-top: 1px solid #ddd;
            font-weight: bold;
            font-size: 16px;
            border-top: 2px solid #333;
            margin-top: 8px;
            padding-top: 8px;
          }
          .footer {
            margin-top: 40px;
            text-align: center;
            font-size: 14px;
            color: #777;
            border-top: 1px solid #eee;
            padding-top: 20px;
          }
          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-header">
          <h1>Facture de Commande</h1>
          <p>Votre Nom de Boutique</p>
          <p>123 Rue du Commerce, Villecentre</p>
          <p>contact@votreboutique.com | 01 23 45 67 89</p>
        </div>
        
        <div class="order-info">
          <div>
            <p><strong>Commande N°:</strong> ${order.reference || 'N/A'}</p>
            <p><strong>Date:</strong> ${formatDate(order.dateBonCommande)}</p>
            <p><strong>Paiement:</strong> ${translatePaymentMethod(order.source || 'Non spécifié')}</p>
          </div>
          <div>
            <p><strong>Client:</strong> ${order.clientFirstName || ''} ${order.clientLastName || ''}</p>
            <p><strong>Téléphone:</strong> ${order.clientNumber || 'Non spécifié'}</p>
          </div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th>Produit</th>
              <th>Prix</th>
              <th>Quantité</th>
              <th style="text-align: right;">Total</th>
            </tr>
          </thead>
          <tbody>
            ${order.items.flatMap(item => 
              Array.from({ length: item.quantity }, (_, i) => `
              <tr>
                <td>
                  <div class="item-name">${item.name}</div>
                  <div class="item-sku">Réf: ${item.reference || 'N/A'}${item.quantity > 1 ? ` (${i + 1}/${item.quantity})` : ''}</div>
                </td>
                <td>${item.price?.toFixed(2) || '0.00'} TND</td>
                <td>1</td>
                <td style="text-align: right;">${item.price?.toFixed(2) || '0.00'} TND</td>
              </tr>
            `)
            ).join('')}
          </tbody>
        </table>
        
        <div class="totals">
          <div>
            <span>Sous-total:</span>
            <span>${subtotal.toFixed(2)} TND</span>
          </div>
          <div>
            <span>TVA:</span>
            <span>${tax.toFixed(2)} TND</span>
          </div>
          <div class="total-row">
            <span>Total:</span>
            <span>${total.toFixed(2)} TND</span>
          </div>
        </div>
        
        <div class="footer">
          <p>Nous vous remercions de votre achat !</p>
          <p>Pour toute question concernant cette commande, veuillez contacter notre service client à support@votreboutique.com</p>
          <p>© ${new Date().getFullYear()} Votre Nom de Boutique</p>
        </div>
        
        <script>
          // Automatically trigger print when the content is loaded
          window.onload = function() {
            setTimeout(function() {
              window.print();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    // Write the invoice to the new window and print it
    printWindow.document.open();
    printWindow.document.write(invoiceContent);
    printWindow.document.close();
  };

  // Handle email functionality
  const handleEmail = () => {
    // In a real application, this would trigger an API call or email service
    if (order) {
      alert(`Un email avec les détails de la commande ${order.reference} serait envoyé à l'adresse du client`);
    }
  };

  // Helper function to translate payment method
  const translatePaymentMethod = (method) => {
    const translations = {
      'Credit Card': 'Carte de Crédit',
      'PayPal': 'PayPal',
      'Bank Transfer': 'Virement Bancaire',
      'Cash on Delivery': 'Paiement à la Livraison',
      'web_admin': 'Administration Web',
      'store': 'Magasin',
      'website': 'Site Web',
      'marketplace': 'Place de Marché'
    };
    return translations[method] || method;
  };

  // Helper function to format date in French
  const formatDate = (dateString) => {
    if (!dateString) return 'Date non spécifiée';

    const date = new Date(dateString);
    // Format date in French style
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Add this helper function near your other helper functions
  const getClientImage = (customer) => {
    const defaultUserImage = "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";

    // If customer has no image property or it's empty, return default
    if (!customer || !customer.image) return defaultUserImage;

    // Handle both string and array formats for images
    if (Array.isArray(customer.image)) {
      return customer.image.length > 0 ? customer.image[0] : defaultUserImage;
    }

    // Handle case when it's a string but empty
    return customer.image.trim() ? customer.image : defaultUserImage;
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';

    const statusLower = status.toLowerCase();

    // Use consistent mapping
    if (statusLower === 'livré' || statusLower === 'delivered') {
      return 'bg-green-100 text-green-800';
    }
    if (statusLower === 'confirmé' || statusLower === 'confirmed') {
      return 'bg-green-100 text-green-800';
    }
    if (statusLower === 'expédié' || statusLower === 'shipped') {
      return 'bg-blue-100 text-blue-800';
    }
    if (statusLower === 'annulé' || statusLower === 'canceled') {
      return 'bg-red-100 text-red-800';
    }
    if (statusLower === 'en attente' || statusLower === 'pending') {
      return 'bg-amber-100 text-amber-800';
    }
    if (statusLower === 'abandonné' || statusLower === 'abandoned') {
      return 'bg-gray-100 text-gray-800';
    }
    if (statusLower === 'prêt à expédier' || statusLower === 'readytoship' || statusLower === 'ready_to_ship') {
      return 'bg-emerald-100 text-emerald-800';
    }
    if (statusLower === 'payé' || statusLower === 'paid') {
      return 'bg-indigo-100 text-indigo-800';
    }
    if (statusLower === 'retour' || statusLower === 'returned') {
      return 'bg-purple-100 text-purple-800';
    }
    if (statusLower === 'retour reçu' || statusLower === 'returnreceived') {
      return 'bg-purple-100 text-purple-800';
    }

    return 'bg-gray-100 text-gray-800';
  };

  // Translate status for display
  const translateStatus = (status) => {
    if (!status) return 'Statut inconnu';

    const translations = {
      // English to French
      'delivered': 'Livré',
      'confirmed': 'Confirmé',
      'shipped': 'Expédié',
      'canceled': 'Annulé',
      'refunded': 'Remboursé',
      'pending': 'En attente',
      'abandoned': 'Abandonné',
      'readytoship': 'Prêt à expédier',
      'readyToShip': 'Prêt à expédier',
      'ready_to_ship': 'Prêt à expédier',
      'paid': 'Payé',
      'returned': 'Retour',
      'returnreceived': 'Retour reçu',
      'returnReceived': 'Retour reçu',

      // French versions (for bidirectional translation)
      'livré': 'Livré',
      'confirmé': 'Confirmé',
      'expédié': 'Expédié',
      'annulé': 'Annulé',
      'remboursé': 'Remboursé',
      'en attente': 'En attente',
      'abandonné': 'Abandonné',
      'prêt à expédier': 'Prêt à expédier',
      'payé': 'Payé',
      'retour': 'Retour',
      'retour reçu': 'Retour reçu'
    };

    // Try to match status directly (case insensitive)
    const statusLower = status.toLowerCase();
    for (const [key, value] of Object.entries(translations)) {
      if (key.toLowerCase() === statusLower) {
        return value;
      }
    }

    // If no match found, return the original status
    return status;
  };

  // Update order status
  const handleUpdateStatus = async (newStatus) => {
    if (!order || statusUpdateLoading) return;

    try {
      setStatusUpdateLoading(true);

      // Get the English status key for the API
      const statusKey = getStatusKeyForApi(newStatus);

      // Call the API to update the status
      const response = await updateOrderStatus(order.id, statusKey);

      if (response.success) {
        // Update local state with the new status (translated to French for display)
        setOrder(prevOrder => ({
          ...prevOrder,
          etat: statusKey,
          status: translateStatus(newStatus)
        }));
      } else {
        alert(`Erreur lors de la mise à jour du statut: ${response.message || 'Veuillez réessayer'}`);
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      alert('Une erreur est survenue lors de la mise à jour du statut.');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // Helper function to get API status key from display status
  const getStatusKeyForApi = (displayStatus) => {
    const statusMap = {
      // French display name to API key
      'En attente': 'pending',
      'Confirmé': 'confirmed',
      'Prêt à expédier': 'readyToShip',
      'Expédié': 'shipped',
      'Livré': 'delivered',
      'Annulé': 'canceled',
      'Abandonné': 'abandoned',
      'Payé': 'paid',
      'Retour': 'returned',
      'Retour reçu': 'returnReceived',

      // English display name to API key (for any English UI elements)
      'Processing': 'pending',
      'Confirmed': 'confirmed',
      'Ready to Ship': 'readyToShip',
      'Shipped': 'shipped',
      'Delivered': 'delivered',
      'Canceled': 'canceled',
      'Cancelled': 'canceled',
      'Abandoned': 'abandoned',
      'Paid': 'paid',
      'Returned': 'returned',
      'Return Received': 'returnReceived'
    };

    return statusMap[displayStatus] || displayStatus;
  };

  // Helper functions for the status tracker
  const isAfterConfirmed = () => {
    const afterConfirmStatuses = ['readytoship', 'readyToShip', 'shipped', 'expédié', 'delivered', 'livré', 'prêt à expédier'];
    return afterConfirmStatuses.some(s => currentStatus.toLowerCase().includes(s.toLowerCase()));
  };

  const isAfterReadyToShip = () => {
    const afterReadyStatuses = ['shipped', 'expédié', 'delivered', 'livré'];
    return afterReadyStatuses.some(s => currentStatus.toLowerCase().includes(s.toLowerCase()));
  };

  const isAfterShipped = () => {
    const afterShippedStatuses = ['delivered', 'livré'];
    return afterShippedStatuses.some(s => currentStatus.toLowerCase().includes(s.toLowerCase()));
  };

  const getStatusCardColor = (status) => {
    if (!status) return 'bg-gray-100 text-gray-800';

    const statusLower = status.toLowerCase();

    if (statusLower.includes('livré') || statusLower.includes('delivered')) {
      return 'bg-green-100 text-green-800 border border-green-200';
    }
    if (statusLower.includes('confirmé') || statusLower.includes('confirmed')) {
      return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
    }
    if (statusLower.includes('expédié') || statusLower.includes('shipped')) {
      return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
    }
    if (statusLower.includes('annulé') || statusLower.includes('canceled')) {
      return 'bg-red-100 text-red-800 border border-red-200';
    }
    if (statusLower.includes('attente') || statusLower.includes('pending')) {
      return 'bg-amber-100 text-amber-800 border border-amber-200';
    }
    if (statusLower.includes('abandonné') || statusLower.includes('abandoned')) {
      return 'bg-gray-100 text-gray-800 border border-gray-200';
    }
    if (statusLower.includes('prêt') || statusLower.includes('ready')) {
      return 'bg-blue-100 text-blue-800 border border-blue-200';
    }
    if (statusLower.includes('payé') || statusLower.includes('paid')) {
      return 'bg-indigo-100 text-indigo-800 border border-indigo-200';
    }
    if (statusLower.includes('retour') || statusLower.includes('return')) {
      return 'bg-purple-100 text-purple-800 border border-purple-200';
    }

    return 'bg-gray-100 text-gray-800 border border-gray-200';
  };

  const getStatusSpecialIcon = (status) => {
    if (!status) return null;

    const statusLower = status.toLowerCase();

    if (statusLower.includes('payé') || statusLower.includes('paid')) {
      return (
        <svg className="inline-block ml-2 h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
        </svg>
      );
    }

    if (statusLower.includes('annulé') || statusLower.includes('canceled')) {
      return (
        <svg className="inline-block ml-2 h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      );
    }

    return null;
  };

  const isAfterReturnInitiated = () => {
    const afterReturnStatuses = ['returnreceived', 'retour reçu'];
    return afterReturnStatuses.some(s => currentStatus.toLowerCase().includes(s.toLowerCase()));
  };

  const isSpecialStatus = () => {
    // These statuses are special and don't follow the normal order flow
    const specialStatuses = ['paid', 'payé', 'returned', 'retour', 'returnreceived', 'retour reçu', 'canceled', 'annulé'];
    return specialStatuses.some(s => currentStatus.toLowerCase().includes(s.toLowerCase()));
  };

  useEffect(() => {
    // Fetch order details from the backend
    const fetchOrderDetails = async () => {
      try {
        setLoading(true);
        const orderData = await fetchOrderById(id);

        if (orderData && !orderData.error) {
          // Convert backend data structure to our frontend model
          const mappedOrder = mapOrderFromApi(orderData);
          setOrder(mappedOrder);
        } else {
          setError(orderData?.error || 'Commande introuvable');
        }
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch order details:', err);
        setError('Échec du chargement des détails de la commande');
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  /**
   * Maps the API response to our frontend model
   */
  const mapOrderFromApi = (apiResponse) => {
    if (!apiResponse) return null;

    const { entet, line } = apiResponse;

    // Map order items from line items
    const items = line?.map(lineItem => {
      const product = lineItem.product;
      const variant = lineItem.productVariant;

      // Better image extraction logic
      let itemImage = null;
      if (variant && variant.imageUrl && variant.imageUrl.length > 0) {
        itemImage = variant.imageUrl[0];
      } else if (product && product.imageUrl && product.imageUrl.length > 0) {
        itemImage = product.imageUrl[0];
      }

      return {
        id: lineItem.id,
        name: variant ?
          `${product.name} - ${variant.name}` :
          product.name,
        reference: variant ?
          variant.reference :
          product.reference,
        price: lineItem.prixUnitaire,
        quantity: lineItem.quantite,
        totalTTC: lineItem.totalTTC,
        totalHt: lineItem.totalHt,
        totalTva: lineItem.totalTva,
        image: itemImage, // Use our extracted image
        sku: variant?.reference || product.reference
      };
    }) || [];

    // Create the mapped order object
    return {
      // Order header info
      id: entet.id,
      reference: entet.reference,
      date: entet.dateBonCommande,
      dateBonCommande: entet.dateBonCommande,
      status: translateStatus(entet.etat),
      etat: entet.etat,

      // Order totals
      totalTTC: entet.totalTTC,
      totalHt: entet.totalHt,
      totalTva: entet.totalTva,
      totalRemise: entet.totalRemise,

      // Order items
      items: items,

      // Customer info
      customer: {
        name: `${entet.clientFirstName || ''} ${entet.clientLastName || ''}`.trim(),
        firstName: entet.clientFirstName,
        lastName: entet.clientLastName,
        phone: entet.clientNumber
      },

      // Addresses
      shippingAddress: {
        name: `${entet.clientFirstName || ''} ${entet.clientLastName || ''}`.trim(),
        street: entet.clientAdresse,
        city: entet.ville,
        state: '',
        zipCode: '',
        country: 'Tunisie'
      },

      // Source and platform info
      source: entet.source,
      platformId: entet.plateformId
    };
  };

  // Helper function to get product image or default image
  const getProductImage = (item) => {
    const defaultImage = "https://www.mon-site-bug.fr/uploads/products/default-product.png";

    // Check if item has an image property and it's not null/undefined/empty
    if (!item.image) return defaultImage;

    // Handle both string and array formats for images
    if (Array.isArray(item.image)) {
      return item.image.length > 0 ? item.image[0] : defaultImage;
    }

    // Handle case when it's a string but empty
    return item.image.trim() ? item.image : defaultImage;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[#174540]"></div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-gray-100 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto bg-white shadow rounded-lg p-8 text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Erreur</h1>
          <p className="text-gray-700 mb-6">{error || 'Commande introuvable'}</p>
          <Link
            to="/admin/orders"
            state={{ fromOrderEdit: true }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none"
          >
            <ChevronLeftIcon className="-ml-1 mr-2 h-5 w-5" aria-hidden="true" />
            Retour aux Commandes
          </Link>
        </div>
      </div>
    );
  }

  // Available status transitions
  const availableStatusOptions = [
    { id: 'pending', name: 'En attente', bgClass: 'bg-amber-100', textClass: 'text-amber-800', borderClass: 'border-amber-300' },
    { id: 'confirmed', name: 'Confirmé', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300' },
    { id: 'readyToShip', name: 'Prêt à expédier', bgClass: 'bg-emerald-100', textClass: 'text-emerald-800', borderClass: 'border-emerald-300' },
    { id: 'shipped', name: 'Expédié', bgClass: 'bg-blue-100', textClass: 'text-blue-800', borderClass: 'border-blue-300' },
    { id: 'delivered', name: 'Livré', bgClass: 'bg-green-100', textClass: 'text-green-800', borderClass: 'border-green-300' },
    { id: 'paid', name: 'Payé', bgClass: 'bg-indigo-100', textClass: 'text-indigo-800', borderClass: 'border-indigo-300' },
    { id: 'canceled', name: 'Annulé', bgClass: 'bg-red-100', textClass: 'text-red-800', borderClass: 'border-red-300' },
    { id: 'returned', name: 'Retour', bgClass: 'bg-purple-100', textClass: 'text-purple-800', borderClass: 'border-purple-300' },
    { id: 'returnReceived', name: 'Retour reçu', bgClass: 'bg-purple-100', textClass: 'text-purple-800', borderClass: 'border-purple-300' }
  ];

  // Get the current status in lowercase for comparison
  const currentStatus = order.etat?.toLowerCase();

  return (
    <div className="bg-gray-100 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center">
            <Link
              to="/admin/orders"
              state={{ fromOrderEdit: true }}
              className="mr-4 inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
            >
              <ChevronLeftIcon className="-ml-1 mr-1 h-5 w-5" aria-hidden="true" />
              Retour
            </Link>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
              Commande {order.reference}
            </h1>
          </div>
          <div className="flex flex-wrap gap-2 sm:space-x-3 w-full sm:w-auto">
            <button
              onClick={handlePrint}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              disabled={!order}
            >
              <PrinterIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
              <span className="sm:inline hidden">Imprimer Facture</span>
              <span className="inline sm:hidden">Imprimer</span>
            </button>
            <button
              onClick={handleEmail}
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none"
              disabled={!order}
            >
              <MailIcon className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
              Email
            </button>
          </div>
        </div>

        {/* Printable content */}
        {order && (
          <div ref={printRef} className="print-content">
            {/* Print header - only visible when printing */}
            <div className="hidden print:block mb-8 text-center">
              <h1 className="text-2xl font-bold">Facture de Commande</h1>
              <p className="text-gray-600">Votre Nom de Boutique</p>
              <p className="text-gray-600">123 Rue du Commerce, Villecentre</p>
              <p className="text-gray-600">contact@votreboutique.com | (555) 123-4567</p>
              <div className="mt-4 border-t border-gray-300 pt-4">
                <h2 className="text-xl font-bold">Commande #{order.reference}</h2>
                <p className="text-gray-600">Date: {formatDate(order.dateBonCommande)}</p>
              </div>
            </div>

            {/* Order Summary Card */}
            <div className="bg-white shadow rounded-lg mb-6 overflow-hidden print:shadow-none print:border print:border-gray-300">
              <div className="p-6 sm:p-8">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-8">
                  <div>
                    <h2 className="text-lg font-medium text-gray-900">Résumé de la commande</h2>
                    <p className="mt-1 text-sm text-gray-500">Placé le {formatDate(order.dateBonCommande)}</p>
                  </div>
                  <div className="mt-4 sm:mt-0">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                </div>

                <div className="border-t border-b border-gray-200 py-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Informations client</h3>
                    <div className="mt-3">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 print:hidden">
                          <img
                            className="h-10 w-10 rounded-full object-cover"
                            src={getClientImage(order.customer)}
                            alt={order.customer?.name || "Client"}
                          />
                        </div>
                        <div className="ml-4 print:ml-0">
                          <p className="text-sm font-medium text-gray-900">{order.customer?.name || "Client non spécifié"}</p>
                          <p className="text-sm text-gray-500">Tél: {order.customer?.phone || "Non spécifié"}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Informations de paiement</h3>
                    <div className="mt-3 text-sm">
                      <p className="text-gray-900">{translatePaymentMethod(order.source || "Méthode non spécifiée")}</p>
                      <p className="text-gray-500 mt-1">Référence: {order.reference || "Non spécifié"}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Adresse De Livraison</h3>
                    <address className="mt-3 text-sm text-gray-500 not-italic">
                      <p>{order.shippingAddress?.name || "Nom non spécifié"}</p>
                      <p>{order.shippingAddress?.street || "Adresse non spécifiée"}</p>
                      <p>{order.shippingAddress?.city || "Ville non spécifiée"}, {order.shippingAddress?.zipCode || ""}</p>
                      <p>{order.shippingAddress?.country || ""}</p>
                    </address>
                  </div>

                  <div>
                    <h3 className="text-sm font-medium text-gray-500">Adresse De Facturation</h3>
                    <address className="mt-3 text-sm text-gray-500 not-italic">
                      <p>{order.shippingAddress?.name || "Nom non spécifié"}</p>
                      <p>{order.shippingAddress?.street || "Adresse non spécifiée"}</p>
                      <p>{order.shippingAddress?.city || "Ville non spécifiée"}, {order.shippingAddress?.zipCode || ""}</p>
                      <p>{order.shippingAddress?.country || ""}</p>
                    </address>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Items Card - with separate ref for printing just this section */}
            <div ref={orderItemsRef} className="bg-white shadow rounded-lg mb-6 overflow-hidden print:shadow-none print:border print:border-gray-300">
              {/* Print-only header for the order items */}
              <div className="hidden print:block px-4 py-5 border-b border-gray-200">
                <h1 className="text-2xl font-bold text-center">Articles commandés</h1>
                <div className="flex justify-between mt-4">
                  <div>
                    <p className="font-medium">Commande n° : {order.reference}</p>
                    <p>Date: {formatDate(order.dateBonCommande)}</p>
                  </div>
                  <div>
                    <p className="font-medium">Client : {order.customer?.name || "Client non spécifié"}</p>
                    <p>Téléphone: {order.customer?.phone || "Non spécifié"}</p>
                  </div>
                </div>
              </div>

              {/* Standard header (hidden in print) */}
              <div className="px-4 py-5 sm:px-6 border-b border-gray-200 print:hidden">
                <h3 className="text-lg font-medium leading-6 text-gray-900">Articles commandés</h3>
                <p className="mt-1 max-w-2xl text-sm text-gray-500">{order.items?.length || 0} Articles</p>
              </div>

              <div className="overflow-x-auto">
                {order.items?.length > 0 ? (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Produit
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Prix
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantité
                        </th>
                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.items.map((item, index) => (
                        <tr key={item.id || index}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-12 w-12 print:hidden">
                                <img
                                  className="h-12 w-12 rounded object-cover"
                                  src={getProductImage(item)}
                                  alt={item.name || "Produit"}
                                />
                              </div>
                              <div className="ml-4 print:ml-0">
                                <div className="text-sm font-medium text-gray-900">{item.name || "Produit non spécifié"}</div>
                                <div className="text-sm text-gray-500">Réf: {item.reference || "N/A"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {(item.price || 0).toFixed(2)} TND
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {item.quantity || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                            {(item.totalTTC || (item.price * item.quantity) || 0).toFixed(2)} TND
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="p-6 text-center text-gray-500">
                    Aucun article trouvé dans cette commande.
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4">
                <div className="flex justify-end text-sm">
                  <div className="w-64">
                    <div className="flex justify-between py-1.5">
                      <dt className="text-gray-500">Sous-total</dt>
                      <dd className="text-gray-900 font-medium">{order.totalHt?.toFixed(2) || '0.00'} TND</dd>
                    </div>
                    <div className="flex justify-between py-1.5 border-t border-gray-200">
                      <dt className="text-gray-500">TVA</dt>
                      <dd className="text-gray-900">{order.totalTva?.toFixed(2) || '0.00'} TND</dd>
                    </div>
                    {order.totalRemise > 0 && (
                      <div className="flex justify-between py-1.5 border-t border-gray-200">
                        <dt className="text-gray-500">Remise</dt>
                        <dd className="text-gray-900">-{order.totalRemise?.toFixed(2) || '0.00'} TND</dd>
                      </div>
                    )}
                    <div className="flex justify-between py-1.5 font-bold border-t border-gray-900 mt-2">
                      <dt>Total</dt>
                      <dd>{order.totalTTC?.toFixed(2) || '0.00'} TND</dd>
                    </div>
                  </div>
                </div>
              </div>

              {/* Print-only footer */}
              <div className="hidden print:block mt-8 text-center text-gray-500">
                <p>Merci pour votre commande !</p>
              </div>
            </div>

            {/* Print footer - only visible when printing */}
            <div className="hidden print:block mt-8 text-center text-sm text-gray-500">
              <p>Merci pour votre confiance !</p>
              <p className="mt-1">Pour toute question concernant cette commande, veuillez contacter le service client à l'adresse support@votresite.com.</p>
              <p className="mt-4">© {new Date().getFullYear()} Le nom de votre magasin</p>
            </div>
          </div>
        )}

        {/* Update Status Card - hidden in print */}
        <div className="bg-white shadow rounded-lg overflow-hidden print:hidden mb-6">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Statut de la commande</h3>
          </div>
          <div className="p-4 sm:p-6">
            {/* Universal Status Notifications */}
            {['paid', 'payé'].includes(currentStatus) && (
              <div className="mb-6 bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                <div className="flex flex-col sm:flex-row items-center justify-center text-indigo-700">
                  <svg className="h-6 w-6 mb-2 sm:mb-0 sm:mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                  </svg>
                  <span className="text-base sm:text-lg font-medium text-center sm:text-left">Cette commande a été marquée comme payée</span>
                </div>
              </div>
            )}

            {['canceled', 'annulé'].includes(currentStatus) && (
              <div className="mb-6 bg-red-50 p-4 rounded-lg border border-red-200">
                <div className="flex flex-col sm:flex-row items-center justify-center text-red-700">
                  <svg className="h-6 w-6 mb-2 sm:mb-0 sm:mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <span className="text-base sm:text-lg font-medium text-center sm:text-left">Cette commande a été annulée</span>
                </div>
              </div>
            )}

            {/* Responsive Timeline - Show horizontal for desktop, vertical for mobile */}
            {/* Desktop view (horizontal timeline) - hidden on mobile */}
            <div className="hidden sm:block relative">
              {/* Status Timeline Line */}
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2" />

              {/* Status Steps */}
              <div className="relative flex justify-between items-center">
                {/* All the existing steps remain the same */}

                {/* Step 1: Pending */}
                <div className="flex flex-col items-center">
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center ${['pending', 'en attente'].includes(currentStatus)
                    ? 'bg-amber-500 ring-4 ring-amber-100'
                    : currentStatus === 'canceled' || currentStatus === 'annulé'
                      ? 'bg-gray-200 text-gray-500'
                      : 'bg-emerald-500 text-white'
                    }`}>
                    {['pending', 'en attente'].includes(currentStatus) ? (
                      <span className="flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                      </span>
                    ) : currentStatus === 'canceled' || currentStatus === 'annulé' ? (
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <div className="text-center mt-2">
                    <span className={`text-sm font-medium ${['pending', 'en attente'].includes(currentStatus) ? 'text-amber-800' : currentStatus === 'canceled' || currentStatus === 'annulé' ? 'text-gray-500' : 'text-gray-800'}`}>
                      En attente
                    </span>
                  </div>
                </div>

                {/* Steps 2-9 remain the same, just include them all here */}
                {/* ... */}
              </div>
            </div>

            {/* Mobile view (vertical timeline) - hidden on desktop */}
            <div className="sm:hidden">
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-gray-200"></div>

                <div className="space-y-6">
                  {/* Step 1: Pending */}
                  <div className="flex items-start">
                    <div className={`relative z-10 mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${['pending', 'en attente'].includes(currentStatus)
                        ? 'bg-amber-500 ring-4 ring-amber-100'
                        : currentStatus === 'canceled' || currentStatus === 'annulé'
                          ? 'bg-gray-200 text-gray-500'
                          : 'bg-emerald-500 text-white'
                      }`}>
                      {['pending', 'en attente'].includes(currentStatus) ? (
                        <span className="flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                        </span>
                      ) : currentStatus === 'canceled' || currentStatus === 'annulé' ? (
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${['pending', 'en attente'].includes(currentStatus) ? 'text-amber-800' : currentStatus === 'canceled' || currentStatus === 'annulé' ? 'text-gray-500' : 'text-gray-800'
                        }`}>
                        En attente
                      </p>
                      {['pending', 'en attente'].includes(currentStatus) && (
                        <p className="text-xs text-gray-500 mt-1">Statut actuel</p>
                      )}
                    </div>
                  </div>

                  {/* Step 2: Confirmed */}
                  <div className="flex items-start">
                    <div className={`relative z-10 mt-0.5 flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${['confirmed', 'confirmé'].includes(currentStatus)
                        ? 'bg-emerald-500 ring-4 ring-emerald-100'
                        : isAfterConfirmed()
                          ? 'bg-emerald-500 text-white'
                          : 'bg-gray-200 text-gray-500'
                      }`}>
                      {['confirmed', 'confirmé'].includes(currentStatus) ? (
                        <span className="flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                        </span>
                      ) : isAfterConfirmed() ? (
                        <svg className="h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <span className="text-sm">2</span>
                      )}
                    </div>
                    <div className="ml-4">
                      <p className={`text-sm font-medium ${['confirmed', 'confirmé'].includes(currentStatus) ? 'text-emerald-800' : isAfterConfirmed() ? 'text-gray-800' : 'text-gray-500'
                        }`}>
                        Confirmé
                      </p>
                      {['confirmed', 'confirmé'].includes(currentStatus) && (
                        <p className="text-xs text-gray-500 mt-1">Statut actuel</p>
                      )}
                    </div>
                  </div>

                  {/* Continue with the rest of the steps in the same format */}
                  {/* Steps 3-9 would follow the same pattern */}
                </div>
              </div>
            </div>

            {/* Current Status Badge - Large and Prominent - same for both views */}
            <div className="mt-8 flex flex-col items-center">
              <span className="text-sm text-gray-500 mb-2">Statut actuel</span>
              <div className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-center shadow-sm ${getStatusCardColor(order.status)}`}>
                <span className="text-base sm:text-lg font-medium">
                  {order.status}
                  {getStatusSpecialIcon(order.status)}
                </span>
              </div>
            </div>

            {/* Last Updated Info */}
            <div className="mt-6 text-sm text-gray-500 text-center">
              Dernière mise à jour: {formatDate(order.lastUpdated || order.dateBonCommande)}
            </div>
          </div>
        </div>

        {/* Order History Card - could be added later */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-6 print:hidden">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Historique de la commande</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">Tous les événements liés à cette commande</p>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <ul className="space-y-4">
              <li className="flex items-start">
                <div className="flex-shrink-0">
                  <span className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
                    <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </span>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-900">Commande créée</p>
                  <p className="text-sm text-gray-500">{formatDate(order.dateBonCommande)}</p>
                </div>
              </li>

              {/* Add more history items based on the order status changes */}
              {order.etat && order.etat !== 'pending' && (
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <span className={`h-8 w-8 rounded-full ${getStatusHistoryColor(order.etat)} flex items-center justify-center`}>
                      {getStatusHistoryIcon(order.etat)}
                    </span>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-900">Statut modifié à {order.status}</p>
                    <p className="text-sm text-gray-500">Date de mise à jour</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function to get appropriate icon for status history
const getStatusHistoryIcon = (status) => {
  const statusLower = status.toLowerCase();

  // Different icons for different statuses
  if (statusLower === 'delivered' || statusLower === 'livré') {
    return (
      <svg className="h-5 w-5 text-green-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5H3V4z" />
        <path d="M13 7a1 1 0 011-1h1a1 1 0 011 1v5a1 1 0 01-1 1h-.05a2.5 2.5 0 01-4.9 0h-1a1 1 0 01-1-1V7a1 1 0 011-1h3z" />
      </svg>
    );
  }

  if (statusLower === 'canceled' || statusLower === 'annulé') {
    return (
      <svg className="h-5 w-5 text-red-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293-1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
      </svg>
    );
  }

  if (statusLower === 'shipped' || statusLower === 'expédié') {
    return (
      <svg className="h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
        <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7h2.535a1 1 0 01.9.7l1.5 4a1 1 0 01-.9 1.3H14v2h1.5a2.5 2.5 0 014.9 0H20a1 1 0 001-1v-3.9a1 1 0 00-.2-.6l-2.3-3.5a1 1 0 00-.8-.4H14V7z" />
      </svg>
    );
  }

  if (statusLower === 'paid' || statusLower === 'payé') {
    return (
      <svg className="h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
      </svg>
    );
  }

  // Default icon
  return (
    <svg className="h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
    </svg>
  );
};

// Helper function to get appropriate color for status history
const getStatusHistoryColor = (status) => {
  const statusLower = status.toLowerCase();

  if (statusLower === 'delivered' || statusLower === 'livré') {
    return 'bg-green-100';
  }
  if (statusLower === 'canceled' || statusLower === 'annulé') {
    return 'bg-red-100';
  }
  if (statusLower === 'shipped' || statusLower === 'expédié') {
    return 'bg-blue-100';
  }
  if (statusLower === 'paid' || statusLower === 'payé') {
    return 'bg-indigo-100';
  }
  if (statusLower === 'returned' || statusLower === 'retour') {
    return 'bg-purple-100';
  }
  if (statusLower === 'returnreceived' || statusLower === 'retour reçu') {
    return 'bg-purple-100';
  }

  return 'bg-gray-100';
};

export default OrderDetailPage;
